import React, { useState, useEffect, useCallback } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { API_BASE } from '../utils/apiBase';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Box,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Spinner,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { POKER_COLORS } from '../theme/designSystem';

interface User {
  id: string;
  username: string;
  photoURL: string | null;
  isFriend?: boolean;
}

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  walletBalance: {
    playCoins: number;
    pkCash: number;
  };
  isVerified: boolean;
  currentUser: FirebaseUser | null;
  onSuccess?: () => void;
}

const TransferModal: React.FC<TransferModalProps> = ({
  isOpen,
  onClose,
  walletBalance,
  isVerified,
  currentUser,
  onSuccess,
}) => {
  const [currency, setCurrency] = useState<'playcoins' | 'pkcash'>('playcoins');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [friends, setFriends] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch friends on mount
  useEffect(() => {
    if (isOpen && currentUser) {
      fetchFriends();
    }
  }, [isOpen, currentUser]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAmount('');
      setNote('');
      setSearchQuery('');
      setSearchResults([]);
      setSelectedUser(null);
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const fetchFriends = async () => {
    if (!currentUser) return;
    
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE}/getFriends`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setFriends(data.friends || []);
      }
    } catch (err) {
      console.error('Failed to fetch friends:', err);
    }
  };

  // Debounced search
  const searchUsers = useCallback(async (query: string) => {
    if (!currentUser || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${API_BASE}/searchUsers?q=${encodeURIComponent(query)}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.users || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setIsSearching(false);
    }
  }, [currentUser]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchUsers(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  const handleTransfer = async () => {
    if (!currentUser || !selectedUser || !amount) {
      setError('Please select a recipient and enter an amount');
      return;
    }

    const transferAmount = parseFloat(amount);
    if (isNaN(transferAmount) || transferAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const balance = currency === 'pkcash' ? walletBalance.pkCash : walletBalance.playCoins;
    if (transferAmount > balance) {
      setError(`Insufficient ${currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins'} balance`);
      return;
    }

    if (currency === 'pkcash' && !isVerified) {
      setError('You must verify your account to send PSS Coins');
      return;
    }

    setIsTransferring(true);
    setError(null);

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE}/transfer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipientId: selectedUser.id,
          amount: transferAmount,
          currency,
          note: note.trim() || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`Successfully sent ${transferAmount.toFixed(2)} ${currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins'} to ${selectedUser.username}`);
        setAmount('');
        setNote('');
        setSelectedUser(null);
        setSearchQuery('');
        
        if (onSuccess) {
          onSuccess();
        }

        // Auto close after success
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(data.error || 'Transfer failed');
      }
    } catch (err) {
      console.error('Transfer failed:', err);
      setError('Transfer failed. Please try again.');
    } finally {
      setIsTransferring(false);
    }
  };

  const selectUser = (user: User) => {
    setSelectedUser(user);
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
  };

  const getMaxAmount = () => {
    return currency === 'pkcash' ? walletBalance.pkCash : walletBalance.playCoins;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg={POKER_COLORS.darkTeal} borderColor={POKER_COLORS.cyan} border="2px">
        <ModalHeader color={POKER_COLORS.cyan}> Send Money</ModalHeader>

        <ModalCloseButton color={POKER_COLORS.cyan} />

        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            {/* Success Message */}
            {success && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                {success}
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            {/* Currency Selection */}
            <Box>
              <Text color="gray.400" fontSize="sm" mb={2}>Currency</Text>
              <HStack spacing={2}>
                <Button
                  flex={1}
                  onClick={() => setCurrency('playcoins')}
                  bg={currency === 'playcoins' ? POKER_COLORS.cyan : 'gray.700'}
                  color={currency === 'playcoins' ? 'white' : 'gray.300'}
                  _hover={{ opacity: 0.9 }}
                >
                  <VStack spacing={0}>
                    <Text fontSize="sm">PlayCoins</Text>
                    <Text fontSize="xs" opacity={0.75}>{walletBalance.playCoins.toFixed(2)}</Text>
                  </VStack>
                </Button>
                <Button
                  flex={1}
                  onClick={() => setCurrency('pkcash')}
                  isDisabled={!isVerified}
                  bg={currency === 'pkcash' ? 'green.500' : 'gray.700'}
                  color={currency === 'pkcash' ? 'white' : 'gray.300'}
                  _hover={{ opacity: 0.9 }}
                  _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                  <VStack spacing={0}>
                    <Text fontSize="sm">PSS Coins</Text>
                    <Text fontSize="xs" opacity={0.75}>
                      {isVerified ? `$${walletBalance.pkCash.toFixed(2)}` : 'Verify to send'}
                    </Text>
                  </VStack>
                </Button>
              </HStack>
            </Box>

            {/* Recipient Selection */}
            <Box>
              <Text color="gray.400" fontSize="sm" mb={2}>Send To</Text>
              
              {selectedUser ? (
                <HStack 
                  bg="gray.700" 
                  p={3} 
                  borderRadius="md" 
                  justify="space-between"
                >
                  <HStack>
                    <Avatar 
                      size="sm" 
                      name={selectedUser.username} 
                      src={selectedUser.photoURL || undefined} 
                    />
                    <Text color="white" fontWeight="medium">{selectedUser.username}</Text>
                  </HStack>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    color="gray.400" 
                    onClick={() => setSelectedUser(null)}
                  >
                    

                  </Button>
                </HStack>
              ) : (
                <Box position="relative">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by username..."
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                  />
                  
                  {/* Search Results Dropdown */}
                  {(searchResults.length > 0 || isSearching) && (
                    <Box 
                      position="absolute" 
                      top="100%" 
                      left={0} 
                      right={0} 
                      mt={1} 
                      bg="gray.700" 
                      borderRadius="md" 
                      maxH="200px" 
                      overflowY="auto"
                      zIndex={10}
                      boxShadow="lg"
                    >
                      {isSearching ? (
                        <HStack p={3} justify="center">
                          <Spinner size="sm" color={POKER_COLORS.cyan} />
                          <Text color="gray.400">Searching...</Text>
                        </HStack>
                      ) : (
                        searchResults.map((user) => (
                          <HStack
                            key={user.id}
                            p={3}
                            cursor="pointer"
                            _hover={{ bg: 'gray.600' }}
                            onClick={() => selectUser(user)}
                          >
                            <Avatar 
                              size="sm" 
                              name={user.username} 
                              src={user.photoURL || undefined} 
                            />
                            <Text color="white">{user.username}</Text>
                            {user.isFriend && (
                              <Badge colorScheme="cyan" ml="auto">Friend</Badge>
                            )}
                          </HStack>
                        ))
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Quick Friends List */}
              {!selectedUser && friends.length > 0 && searchQuery.length < 2 && (
                <Box mt={3}>
                  <Text color="gray.500" fontSize="xs" mb={2}>Quick Send to Friends</Text>
                  <Wrap spacing={2}>
                    {friends.slice(0, 6).map((friend) => (
                      <WrapItem key={friend.id}>
                        <Button
                          size="sm"
                          variant="outline"
                          borderColor="gray.600"
                          color="white"
                          leftIcon={
                            <Avatar 
                              size="xs" 
                              name={friend.username} 
                              src={friend.photoURL || undefined} 
                            />
                          }
                          onClick={() => selectUser(friend)}
                          _hover={{ bg: 'gray.700' }}
                        >
                          {friend.username}
                        </Button>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              )}
            </Box>

            {/* Amount Input */}
            <Box>
              <Text color="gray.400" fontSize="sm" mb={2}>Amount</Text>
              <InputGroup>
                <InputLeftElement color="gray.500">$</InputLeftElement>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  bg="gray.700"
                  border="none"
                  color="white"
                  fontSize="lg"
                  _placeholder={{ color: 'gray.500' }}
                />
                <Button
                  position="absolute"
                  right={2}
                  top="50%"
                  transform="translateY(-50%)"
                  size="xs"
                  colorScheme="cyan"
                  variant="ghost"
                  onClick={() => setAmount(getMaxAmount().toString())}
                >
                  MAX
                </Button>
              </InputGroup>
              <Text color="gray.500" fontSize="xs" mt={1}>
                Available: {getMaxAmount().toFixed(2)} {currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins'}
              </Text>
            </Box>

            {/* Note Input */}
            <Box>
              <Text color="gray.400" fontSize="sm" mb={2}>Note (optional)</Text>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What's this for?"
                maxLength={100}
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.500' }}
              />
            </Box>

            {/* Send Button */}
            <Button
              onClick={handleTransfer}
              isDisabled={!selectedUser || !amount || isTransferring}
              isLoading={isTransferring}
              loadingText="Sending..."
              bgGradient="linear(to-r, cyan.500, green.500)"
              color="white"
              size="lg"
              w="100%"
              _hover={{ opacity: 0.9 }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            >
              Send {amount ? parseFloat(amount).toFixed(2) : '0.00'} {currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins'}
            </Button>

            {/* Info */}
            <Text color="gray.500" fontSize="xs" textAlign="center">
              No fees • Instant transfer • Recipient will be notified
            </Text>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TransferModal;